/**
 * ProcessOverview — Processes module landing page.
 *
 * Aligned with AttendanceOverview / IntegrationsOverview / SystemOverview
 * design language (header + setup guide + KPI row + 2 two-column widget
 * rows). Previously this was 6 KPI cards plus a "see the main Dashboard"
 * link, which was wrong — Dashboard is cross-module; operators entering
 * /processes/overview want the Processes-specific landing with actionable
 * widgets, not a redirect.
 *
 * Data flow: client-side aggregation from the 3 real endpoints
 * (instances / tasks / requests) + definitions. Same pattern as the
 * Dashboard Process widgets (they aggregate the same way). When the
 * engine (ADR-016+) ships a real /v1/processes/overview response, any
 * single widget here can switch to it without changing its shape —
 * each widget consumes arrays, not the raw /overview dict.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccessTimeOutlined,
  AccountTreeOutlined,
  AddOutlined,
  ArrowForwardOutlined,
  AssignmentOutlined,
  CancelOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  RefreshOutlined,
  TrendingDownOutlined,
  TrendingUpOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ApiError, processesApi } from '../../../api';
import type { ApprovalRoutingHealth } from '../../../api/processes';

import { isActionableRequestStatus } from '../../../api/processes';
import SetupGuide from '../../../components/onboarding/SetupGuide';
import type {
  ProcessDefinition,
  ProcessInstance,
  ProcessRequest,
  ProcessTask,
} from '../../../api/processes';
import { getSystemRequestTitle, translatePriority } from '../processDisplay';
import { translateApiError } from '../../../i18n/translateApiError';

/**
 * The resolver error codes this build ships a sentence for, and the one everything else becomes.
 *
 * A WHITELIST rather than `t(..., { defaultValue })`. The module forbids `defaultValue` for a reason
 * recorded in `moduleConventions.test.ts` and in i18n-nine-locales §7: a default silently props up a key
 * that exists in NO locale, so the English reads fine and every other language shows the fallback — the
 * gap is invisible to the locale-parity check precisely because nothing is missing from `en`.
 *
 * Mapping to a REAL key instead means a new backend error code degrades to a sentence that all nine
 * locales carry, and `routingHealthWarning.test.ts` asserts that set stays complete.
 */
const ROUTING_REASON_CODES = [
  'manager_unresolvable',
  'process_role_empty',
  'process_role_not_found',
  'approver_inactive',
];

function routingReasonKey(code: string | undefined | null): string {
  return code && ROUTING_REASON_CODES.indexOf(code) >= 0 ? code : 'approver_unresolvable';
}

// ---------------------------------------------------------------------------
// NO local setup guide here. 2026-08-27: this file carried a SECOND one —
// `setupSteps`, two interfaces, a per-org localStorage dismiss pair, a
// `show-setup-guide` listener and a `stepsWithState` memo — and NONE of it was
// ever rendered. The chain ended at `const _doneCount = ...`, an underscore-
// prefixed dead variable, so the guide the owner sees at the top of this page has
// always been `<SetupGuide section="processes" />` (line ~506), driven by the
// backend `onboarding_steps_catalog`.
//
// That made it the worst kind of duplicate: a second source of truth for "what
// order do you set this up in" that no user could ever check against the first.
// I reordered it on 2026-08-26 believing it was the visible one, which is the
// clearest possible demonstration of why dead code is not harmless.
//
// The backend guide is the single source: real completion detection via
// `onboarding_step_progress`, nine locales, a per-section dismiss, and since
// 2026-08-27 a pruner that deactivates steps a release no longer ships. Four other
// overview pages render the same component; this one now matches them.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// WidgetShell + ViewAllBtn primitives — borrowed verbatim from
// AttendanceOverview so the two pages render identically.
// ---------------------------------------------------------------------------

const WidgetShell: React.FC<{
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  flex?: boolean;
}> = ({ title, action, children, flex }) => (
  <Paper sx={{
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'rgba(0,0,0,0.2)',
    boxShadow: 'none',
    overflow: 'hidden',
    ...(flex ? { flex: 1, display: 'flex', flexDirection: 'column', width: '100%' } : {}),
  }}>
    <Box sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary', letterSpacing: '0.01em' }}>
        {title}
      </Typography>
      {action}
    </Box>
    {children}
  </Paper>
);

const ViewAllBtn: React.FC<{ to: string; label?: string }> = ({ to, label }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const display = label ?? t('processes:overview.viewAll');
  return (
    <Button size="small" endIcon={<ArrowForwardOutlined sx={{ fontSize: '0.8rem' }} />}
      onClick={() => navigate(to)}
      sx={{
        textTransform: 'none',
        fontSize: '0.75rem',
        color: 'primary.main',
        p: 0,
        minWidth: 0,
        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
      }}>
      {display}
    </Button>
  );
};

// ---------------------------------------------------------------------------
// Helpers — status mapping and age formatting mirror the Dashboard
// widgets so operators see consistent labels across surfaces.
// ---------------------------------------------------------------------------

type EventType = 'started' | 'completed' | 'rejected' | 'assigned' | 'escalated';

// Event icon + color palette is module-level (renders identically across
// renders). The `label` is resolved at render time via t() since the
// module level can't access the i18n hook.
const eventConfig: Record<EventType, { icon: React.ReactNode; color: string }> = {
  started:   { icon: <PlayCircleOutlined sx={{ fontSize: '0.95rem' }} />, color: '#2196F3' },
  completed: { icon: <CheckCircleOutlined sx={{ fontSize: '0.95rem' }} />, color: '#4CAF50' },
  rejected:  { icon: <CancelOutlined sx={{ fontSize: '0.95rem' }} />,     color: '#F44336' },
  assigned:  { icon: <AssignmentOutlined sx={{ fontSize: '0.95rem' }} />, color: '#FF9800' },
  escalated: { icon: <PauseCircleOutlined sx={{ fontSize: '0.95rem' }} />, color: '#9C27B0' },
};

function mapStatusToEvent(s: string | null | undefined): EventType {
  if (s === 'completed' || s === 'approved') return 'completed';
  if (s === 'rejected' || s === 'cancelled' || s === 'canceled') return 'rejected';
  if (s === 'failed' || s === 'exception' || s === 'error') return 'escalated';
  if (s === 'waiting' || s === 'pending') return 'assigned';
  return 'started';
}

function ageString(iso: string | null | undefined, locale: string, t: (k: string, opts?: any) => string): string {
  if (!iso) return '—';
  const d = Date.parse(iso);
  if (!Number.isFinite(d)) return '—';
  const diffMs = Date.now() - d;
  const minutes = diffMs / 60_000;
  if (minutes < 1) return t('processes:overview.ageJustNow');
  if (minutes < 60) return t('processes:overview.ageMinutes', { count: Math.round(minutes) });
  const hours = minutes / 60;
  if (hours < 24) return t('processes:overview.ageHours', { count: Math.round(hours) });
  const days = hours / 24;
  if (days < 7) return t('processes:overview.ageDays', { count: Math.round(days) });
  return new Date(iso).toLocaleDateString(locale);
}

function initialsOf(seed: string | null | undefined): string {
  if (!seed) return '•';
  const parts = seed.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return seed.slice(0, 2).toUpperCase();
}

function avatarColor(seed: string): string {
  const palette = ['#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336', '#00BCD4', '#795548'];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
}

function slaBarColor(rate: number): '#4CAF50' | '#FF9800' | '#F44336' {
  if (rate >= 95) return '#4CAF50';
  if (rate >= 85) return '#FF9800';
  return '#F44336';
}

function nameFor(id: string | null | undefined, workers: Map<string, string>): string {
  if (!id) return '—';
  return workers.get(id) ?? id.slice(0, 8);
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

const ProcessOverview: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [instances, setInstances] = useState<ProcessInstance[]>([]);
  const [tasks, setTasks] = useState<ProcessTask[]>([]);
  const [requests, setRequests] = useState<ProcessRequest[]>([]);
  const [definitions, setDefinitions] = useState<ProcessDefinition[]>([]);
  // Real-time KPIs from backend `/v1/processes/overview` (Phase E4).
  // Authoritative source — supersedes the client-side aggregation
  // for the KPI row (still kept as fallback for trend widgets).
  const [overviewKpis, setOverviewKpis] = useState<{
    by_status: Record<string, number>;
    today: number;
    this_week: number;
    this_month: number;
    pending_tasks: number;
  } | null>(null);
  const [workerById, setWorkerById] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /**
   * Which active processes cannot currently route an approval.
   *
   * `null` means "not known" — the request failed, or has not returned — and renders nothing. An absent
   * warning must never be indistinguishable from a clean bill of health.
   */
  const [routingHealth, setRoutingHealth] = useState<ApprovalRoutingHealth | null>(null);
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [insts, tks, reqs, defs, ov, health] = await Promise.all([
        processesApi.processInstancesApi.list(),
        processesApi.processTasksApi.list(),
        processesApi.processRequestsApi.list(),
        processesApi.processDefinitionsApi.list(),
        processesApi.processAnalyticsApi.overview().catch(() => null),
        // `.catch(() => null)` like the analytics call above: a warning that cannot load must not take
        // the page with it, and `null` renders nothing rather than a false "all clear".
        processesApi.processDefinitionsApi.routingHealth().catch(() => null),
      ]);
      setInstances(insts);
      setTasks(tks);
      setRequests(reqs);
      setDefinitions(defs);
      setRoutingHealth(health);
      if (ov && ov.kpis) {
        setOverviewKpis(ov.kpis as any);
      }
      // Lazy-load workers for FK→name resolution. Failure here just
      // means the tables show ids instead of names — non-fatal.
      try {
        const mod = await import('../../../api');
        const workers = await mod.organizationApi.workersApi.list();
        const m = new Map<string, string>();
        for (const e of workers) {
          m.set(e.id, `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || e.id);
        }
        setWorkerById(m);
      } catch {
        /* tolerate — id fallback is fine */
      }
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('processes:overview.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ----- Derived data -----

  const defNameById = useMemo(
    () => new Map(definitions.map((d) => [d.id, d.name])),
    [definitions],
  );


  const kpis = useMemo(() => {
    const now = Date.now();
    // Prefer authoritative backend KPIs (alembic 028+ engine, Phase E4)
    // when available; fall back to client-side aggregation for legacy
    // tenants whose backend hasn't been redeployed yet.
    const byStatus = overviewKpis?.by_status ?? {};
    const active = overviewKpis
      ? (byStatus.running ?? 0) + (byStatus.waiting ?? 0) + (byStatus.suspended ?? 0) + (byStatus.in_progress ?? 0)
      : instances.filter((i) => ['running', 'waiting', 'suspended', 'in_progress'].includes(i.status)).length;
    const pending = overviewKpis
      ? overviewKpis.pending_tasks
      : requests.filter((r) => isActionableRequestStatus(r.status)).length;
    const overdue = tasks.filter((t) => {
      if (!t.due_date) return false;
      const d = Date.parse(t.due_date);
      return Number.isFinite(d) && d < now && ['pending', 'in_progress', 'review'].includes(t.status);
    }).length;
    const sla = instances.filter((i) => i.sla_status === 'breached').length;
    const completedTotal = overviewKpis
      ? (byStatus.completed ?? 0)
      : instances.filter((i) => i.status === 'completed').length;
    const expiring24h = tasks.filter((t) => {
      if (!t.due_date) return false;
      const d = Date.parse(t.due_date);
      return Number.isFinite(d) && d >= now && d - now <= 24 * 3_600_000 &&
        ['pending', 'in_progress', 'review'].includes(t.status);
    }).length;
    return [
      { label: t('processes:overview.kpis.active'), value: active, delta: 0, color: '#2196F3', path: '/processes/instances/active' },
      // These three pointed at `/me/approvals` / `/me/tasks` until 2026-07-31.
      // This page IS an admin route, so every card here must stay in the admin
      // shell: `resolveRoleLanding` redirects any admin-tier role out of `/me/*`
      // to /dashboard, and PortalBoundary does it with `replace`, so the click
      // looked like nothing happened. The give-away was in this very array —
      // the other three cards already used `/processes/instances/*`.
      { label: t('processes:overview.kpis.pendingApprovals'), value: pending, delta: 0, color: '#FF9800', path: '/processes/instances/active' },
      { label: t('processes:overview.kpis.overdueTasks'), value: overdue, delta: 0, color: '#F44336', path: '/processes/instances/active' },
      { label: t('processes:overview.kpis.slaBreached'), value: sla, delta: 0, color: '#795548', path: '/processes/instances/failed' },
      { label: t('processes:overview.kpis.completed'), value: completedTotal, delta: 0, color: '#4CAF50', path: '/processes/instances/completed' },
      { label: t('processes:overview.kpis.expiring24h'), value: expiring24h, delta: 0, color: '#9C27B0', path: '/processes/instances/active' },
    ];
  }, [overviewKpis, instances, tasks, requests, t]);

  const byDefinition = useMemo(() => {
    const byDef = new Map<string, { defId: string; name: string; total: number; active: number; breached: number }>();
    for (const i of instances) {
      const defId = i.definition_id ?? '__unknown';
      const prev = byDef.get(defId) ?? {
        defId,
        name: defNameById.get(defId) ?? t('processes:overview.unknownDefinition'),
        total: 0, active: 0, breached: 0,
      };
      prev.total += 1;
      if (['running', 'waiting', 'suspended', 'in_progress'].includes(i.status)) prev.active += 1;
      if (i.sla_status === 'breached') prev.breached += 1;
      byDef.set(defId, prev);
    }
    return Array.from(byDef.values()).sort((a, b) => b.total - a.total);
  }, [instances, defNameById, t]);

  const pendingApprovals = useMemo(() => {
    return requests
      .filter((r) => isActionableRequestStatus(r.status))
      .sort((a, b) => {
        const ta = Date.parse(a.submitted_at ?? a.created_at ?? '') || 0;
        const tb = Date.parse(b.submitted_at ?? b.created_at ?? '') || 0;
        return tb - ta;
      })
      // 10, not 5. The card is stretched to its row sibling's height and five rows left a
      // ~440px void under the last one; the container now scrolls, so a longer list is bounded
      // by the card rather than by this number.
      .slice(0, 10);
  }, [requests]);

  const slaRows = useMemo(() => {
    const byDef = new Map<string, { defId: string; name: string; total: number; atRisk: number; breached: number }>();
    for (const i of instances) {
      if (!i.definition_id) continue;
      const prev = byDef.get(i.definition_id) ?? {
        defId: i.definition_id,
        name: defNameById.get(i.definition_id) ?? t('processes:overview.unknownDefinition'),
        total: 0, atRisk: 0, breached: 0,
      };
      prev.total += 1;
      if (i.sla_status === 'breached') prev.breached += 1;
      else if (i.sla_status === 'at_risk') prev.atRisk += 1;
      byDef.set(i.definition_id, prev);
    }
    return Array.from(byDef.values()).map((r) => ({
      ...r,
      compliance: r.total === 0 ? 100 : Math.round(((r.total - r.breached - r.atRisk) / r.total) * 100),
    })).sort((a, b) => a.compliance - b.compliance);
  }, [instances, defNameById, t]);

  const recentActivity = useMemo(() => {
    const stamp = (r: ProcessInstance) =>
      Date.parse(r.completed_at ?? r.started_at ?? r.created_at ?? '') || 0;
    return [...instances]
      .sort((a, b) => stamp(b) - stamp(a))
      .slice(0, 8);
  }, [instances]);

  // ----- Render -----

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  const totalPending = requests.filter((r) => isActionableRequestStatus(r.status)).length;

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
      py: '12px', width: '100%',
    }}>
      {/* Header */}
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>
            {t('processes:overview.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('processes:overview.subtitle')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton size="small" onClick={fetchAll} disabled={loading}
              sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}>
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" size="small" startIcon={<AddOutlined />}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
            onClick={() => navigate('/processes/designer')}>
            {t('processes:overview.newProcess')}
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* APPROVAL ROUTING WARNING — the reminder that arrives before a worker's submission fails.
          An SMB runs out of approvers through ORDINARY operations: a flat org has no manager's manager
          (and `Leave Request` ships a level-2 step), a seat can be emptied deliberately, and a seat
          empties BY ITSELF when its only occupant is terminated — nobody touched the routing.

          `severity="warning"`, not error: nothing is broken yet. The processes still run for anyone whose
          steps resolve; what this says is which ones would fail if submitted now.

          Rendered only when the answer is KNOWN and negative. `routingHealth === null` (request failed or
          still in flight) shows nothing — an absent warning must never read as a clean bill of health.

          The CTA goes to `/processes/roles`, which is where the fix is, and which the role holding
          `processes.definitions.manage` can open (svdy-ui §12.10 — a link an admin-shell widget offers
          must be a route that role actually resolves to). */}
      {routingHealth && !routingHealth.healthy && (routingHealth.blocked_steps ?? []).length > 0 && (
        <Alert
          severity="warning"
          sx={{ width: '100%', mb: 2 }}
          action={(
            <Button color="inherit" size="small" onClick={() => navigate('/processes/roles')}>
              {t('processes:routingHealth.fixSeats')}
            </Button>
          )}
        >
          <AlertTitle sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
            {t('processes:routingHealth.title', { count: (routingHealth.blocked_steps ?? []).length })}
          </AlertTitle>
          <Stack spacing={0.25} sx={{ mt: 0.5 }}>
            {(routingHealth.blocked_steps ?? []).map((step) => (
              <Typography key={`${step.definition_id}-${step.node_id}`} variant="body2">
                <strong>{step.definition_name || step.definition_id}</strong>
                {step.step_label ? ` · ${step.step_label}` : ''}
                {' — '}
                {t(`processes:routingHealth.reason.${routingReasonKey(step.code)}`, {
                  // The seat's own name when the server knows it, so the sentence says WHICH one to staff.
                  role: step.role_code ?? '',
                })}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      {/* Setup Guide — backend-driven (ADR-019 §D8) */}
      <SetupGuide section="processes" />

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {kpis.map((k) => (
          <Grid item xs={6} sm={4} md={2} key={k.label} sx={{ display: 'flex' }}>
            <Paper elevation={0} onClick={() => navigate(k.path)} sx={{
              p: 1.5, borderRadius: 2, bgcolor: 'background.paper', cursor: 'pointer', width: '100%',
              border: '1px solid', borderColor: 'rgba(0,0,0,0.2)',
              transition: 'box-shadow 80ms ease, border-color 80ms ease',
              '&:hover': { borderColor: k.color },
            }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', display: 'block', mb: 0.5 }}>
                {k.label}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: k.color, lineHeight: 1 }}>{k.value}</Typography>
                {k.delta !== 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: k.delta > 0 ? 'error.main' : 'success.main' }}>
                    {k.delta > 0 ? <TrendingUpOutlined sx={{ fontSize: '0.85rem' }} /> : <TrendingDownOutlined sx={{ fontSize: '0.85rem' }} />}
                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{Math.abs(k.delta)}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Row 2: By Definition (md=4) + Pending Approvals (md=8) */}
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <WidgetShell flex title={t('processes:overview.cards.byDefinition')} action={<ViewAllBtn to="/processes/designer" />}>
            <Box sx={{ p: 1.5 }}>
              {byDefinition.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('processes:overview.empty.noDefinitions')}
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {byDefinition.slice(0, 6).map((row) => (
                    <Paper key={row.defId} elevation={0}
                      onClick={() => navigate('/processes/instances/active')}
                      sx={{
                        p: 1.25, borderRadius: 2, bgcolor: 'background.paper', cursor: 'pointer',
                        border: '1px solid', borderColor: 'divider',
                        borderLeft: '3px solid',
                        borderLeftColor: row.breached > 0 ? '#F44336' : row.active > 0 ? '#2196F3' : '#BDBDBD',
                        transition: 'border-color 80ms ease',
                        '&:hover': { borderColor: 'primary.main' },
                      }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', display: 'block' }} noWrap>
                            {row.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                            {t('processes:overview.summaryActiveTotal', { active: row.active, total: row.total })}
                            {row.breached > 0 && t('processes:overview.summaryBreachedSuffix', { count: row.breached })}
                          </Typography>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2196F3', lineHeight: 1 }}>
                          {row.active}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          </WidgetShell>
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <WidgetShell flex title={t('processes:overview.cards.pendingApprovals')} action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {totalPending > 0 ? t('processes:overview.ofPagination', { shown: Math.min(10, totalPending), total: totalPending }) : '0'}
              </Typography>
              <ViewAllBtn to="/processes/instances/active" />
            </Box>
          }>
              {/* `flex: 1` + internal scroll, so the content fills the card instead of leaving a
                  void under the last row. The card is stretched by `WidgetShell flex` to match
                  its row sibling — which renders 6 stacked Paper cards and is far taller — and
                  §11.2 wants those bottom edges level, so the stretch is right and the CONTENT
                  had to grow to meet it. Scrolling internally means a tenant with 50 pending
                  approvals cannot blow the card up either.

                  `minWidth: 420`, not 650. 650 was mine, from a batch pass this evening that
                  gave every table in the module the same floor — correct for a full-width page
                  table and wrong here: this card is `md={6}`, so ~570px on a 13" screen, and a
                  650px floor is exactly what produced the horizontal scrollbar and clipped the
                  Age column in the owner's screenshot. A floor has to fit its container. */}
            {pendingApprovals.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('processes:overview.empty.noPendingApprovals')}
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
                <Table sx={{ minWidth: 420 }} size="small">
                  <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
                    <TableRow>
                      <TableCell>{t('processes:overview.tableHeaders.requester')}</TableCell>
                      <TableCell>{t('processes:overview.tableHeaders.request')}</TableCell>
                      <TableCell>{t('processes:overview.tableHeaders.definition')}</TableCell>
                      <TableCell>{t('processes:overview.tableHeaders.age')}</TableCell>
                      <TableCell>{t('processes:overview.tableHeaders.priority')}</TableCell>
                      <TableCell align="center">{t('processes:overview.tableHeaders.open')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingApprovals.map((row) => {
                      const requesterName = nameFor(row.requester_id, workerById);
                      return (
                        <TableRow
                          key={row.id}
                          hover
                          // Every column on one line; the prose column opts back in below. On
                          // the ROW so a column added later inherits it.
                          sx={{ cursor: 'pointer',
                                '& .MuiTableCell-root': { whiteSpace: 'nowrap' } }}
                          onClick={() => navigate(
                          row.instance_id
                            ? `/processes/instances/${row.instance_id}`
                            : '/processes/instances/active',
                        )}>
                          {/* The prose column: bounded so it wraps at a sane measure instead of
                        stretching the row. */}
                          <TableCell sx={{ maxWidth: 320, whiteSpace: 'normal' }}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Avatar sx={{
                                width: 22, height: 22, fontSize: '0.65rem',
                                bgcolor: avatarColor(requesterName), color: 'white', fontWeight: 600,
                              }}>
                                {initialsOf(requesterName)}
                              </Avatar>
                              <Typography variant="caption" sx={{ fontWeight: 500 }}>{requesterName}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.primary">{getSystemRequestTitle(t, row, row.title)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={defNameById.get(row.definition_id ?? '') ?? '—'}
                              size="small" variant="outlined"
                              sx={{ height: 18, fontSize: '0.7rem', borderRadius: 1, '& .MuiChip-label': { px: 0.75 } }}
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <AccessTimeOutlined sx={{ fontSize: '0.75rem', color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">{ageString(row.submitted_at ?? row.created_at, i18n.language, t)}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={translatePriority(t, row.priority ?? 'normal')} size="small"
                              variant="outlined"
                              color={row.priority === 'urgent' || row.priority === 'high' ? 'error'
                                : row.priority === 'medium' ? 'warning' : 'default'}
                              sx={{ height: 18, fontSize: '0.7rem', borderRadius: 1,
                                    '& .MuiChip-label': { px: 0.75 } }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" sx={{ p: 0.25 }}>
                              <ArrowForwardOutlined sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </WidgetShell>
        </Grid>
      </Grid>

      {/* Row 3: SLA Compliance (md=8) + Recent Activity (md=4) */}
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <WidgetShell flex title={t('processes:overview.cards.slaCompliance')} action={<ViewAllBtn to="/processes/analytics" />}>
            <Box sx={{ p: 1.5 }}>
              {slaRows.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('processes:overview.empty.noSlaData')}
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {slaRows.slice(0, 6).map((row) => {
                    const barColor = slaBarColor(row.compliance);
                    return (
                      <Box key={row.defId}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                            <AccountTreeOutlined sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
                              {row.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                              {t('processes:overview.slaSummary', { total: row.total, atRisk: row.atRisk, breached: row.breached })}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: barColor, minWidth: 34, textAlign: 'right' }}>
                            {row.compliance}%
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate" value={row.compliance}
                          sx={{
                            height: 6, borderRadius: 8, bgcolor: 'grey.100',
                            '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 8 },
                          }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </WidgetShell>
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <WidgetShell flex title={t('processes:overview.cards.recentActivity')} action={<ViewAllBtn to="/processes/instances/active" />}>
            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {recentActivity.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('processes:overview.empty.noActivity')}
                </Typography>
              ) : (
                recentActivity.map((inst) => {
                  const ev = mapStatusToEvent(inst.status);
                  const cfg = eventConfig[ev];
                  const evLabel = t(`processes:overview.events.${ev}`);
                  const defName = defNameById.get(inst.definition_id ?? '') ?? t('processes:overview.unknownDefinition');
                  const stampStr = ageString(inst.completed_at ?? inst.started_at ?? inst.created_at, i18n.language, t);
                  return (
                    <Stack key={inst.id} direction="row" spacing={1} alignItems="flex-start">
                      <Box sx={{
                        width: 24, height: 24, borderRadius: '50%',
                        bgcolor: `${cfg.color}15`, color: cfg.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        {cfg.icon}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" sx={{ fontWeight: 600 }} noWrap>
                            {defName}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', whiteSpace: 'nowrap', ml: 0.5 }}>
                            {stampStr}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }} noWrap>
                          {evLabel} · {inst.instance_number ?? inst.id.slice(0, 10)}
                        </Typography>
                      </Box>
                    </Stack>
                  );
                })
              )}
            </Box>
          </WidgetShell>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProcessOverview;
